import type { GridView } from "@appretio/shared";
import { Dialog, IconButton } from "@appretio/ui-kit";
import { useState } from "react";

/** The words a delete confirm uses for its list's kind of view. */
export interface DeleteViewTexts {
  /** Icon tooltip and dialog title, e.g. "Delete view". */
  title: string;
  /** The confirm's question, given the view's name. */
  message: (name: string) => string;
}

interface DeleteViewButtonProps {
  /** The applied saved view. ``undefined`` hides the button. */
  view?: GridView;
  /** What the confirm calls the view and what deleting leaves behind. */
  texts: DeleteViewTexts;
  /** Deletes the view. */
  onDelete: (viewId: string) => Promise<unknown>;
  /** Whether the delete is in flight. */
  isPending: boolean;
}

/** Trashcan icon that deletes the applied view, behind a confirm. */
export function DeleteViewButton({
  view,
  texts,
  onDelete,
  isPending
}: DeleteViewButtonProps) {
  const [open, setOpen] = useState(false);

  if (!view) return null;

  return (
    <>
      <IconButton
        iconName="trash"
        variant="tertiary"
        size="xs"
        onClick={() => setOpen(true)}
        title={texts.title}
        aria-label={texts.title}
      />
      <Dialog
        open={open}
        title={texts.title}
        onOpenChange={setOpen}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isPending}
        // A failed delete (404 from another tab, network) must still let
        // the dialog close; the hook's toast reports it.
        onConfirm={async () => {
          await onDelete(view.view_id).catch(() => undefined);
        }}
      >
        <p>{texts.message(view.name)}</p>
      </Dialog>
    </>
  );
}
